import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import { ContentContainer } from '@sorare/core/src/components/landing/LandingMultiSport/ui';

const StyledContainer = styled(ContentContainer)`
  & p {
    margin-bottom: 1em;
    margin-top: 0.5em;
    line-height: 1.5em;
    color: var(--c-nd-400-opaque);
  }
  & ol li {
    list-style: decimal;
    color: var(--c-nd-400-opaque);
  }
`;

const messages = defineMessages({
  content: {
    id: 'Baseball.FAQ.content',
    defaultMessage: `

## What is Sorare?

Sorare offers MLB fans an extraordinary way to engage with the world’s top professional baseball league and players, blending the excitement of the sport with the strategic elements of lineup-building and team management.
With Sorare, you own and build your dream MLB team with officially licensed digital player cards from all 30 MLB clubs – buying, selling, trading, and competing with them – to win rewards like cash, tickets, and VIP experiences such as attending the MLB All-Star game and meeting star players. (See our Sorare MLB guide [here](https://mlbguide.sorare.com/).)
Are you ready to step up to the plate and manage your own MLB team? With Sorare, you dive into a world where every pitch, hit, and home run counts in your journey to fantasy glory.

## What is Fantasy Baseball?

Fantasy baseball, the original fantasy sports game also originally known as rotisserie baseball, allows you to step into the shoes of a team manager –  and in the case of Sorare MLB, into the shoes of a real owner – as you own your digital player cards used in Sorare fantasy competitions.

In fantasy baseball, your objective is simple yet challenging: assemble a team of real-life MLB players and compete against friends and rivals for bragging rights and, in Sorare, amazing rewards. As these players perform in actual games, your fantasy team earns points based on their on-field actions like hits, RBIs, wins, saves, and dozens of other stats. It's a test of your baseball knowledge, strategic planning, and adaptability. It's an engaging way to experience MLB, combining your love for the game with the challenge of team management.
Fantasy MLB has become a beloved pastime for millions, merging the thrill of baseball with the challenge of strategy and management.

## Why Sorare is your go-to Fantasy MLB game

Sorare stands out in the world of fantasy baseball. We offer a unique and immersive experience, where you not only manage a fantasy MLB team but also collect and trade officially licensed digital player cards. This feature adds an extra layer of strategy and excitement, allowing you to truly own your player cards and team.

Our platform takes the fantasy experience beyond the ordinary, empowering baseball fans to engage with the game and other MLB fans on a deeper level. At Sorare, fantasy baseball is not just about winning competitions and rewards; it's about building a collection, strategizing, and being part of a vibrant fantasy MLB community.


## How to Play Fantasy Baseball with Sorare

It’s free and easy to start your fantasy MLB journey with Sorare:
 1. Sign up for free and build a team of players using cards you own
 1. Set a lineup and enter a competition (join anytime, competitions are twice-weekly)
 1. Track your team’s score as you earn points and rewards based on real-life performances
 1. Boost your team by buying, selling, and trading player cards on Sorare’s [Marketplace](https://sorare.com/mlb/market)

Here’s a more detailed how-to-play [guide](https://mlbguide.sorare.com/).

### Building Your Fantasy MLB Dream Team

Your success in fantasy baseball hinges on smart team-building. Pay attention to player statistics – both recent performances and season-long. But don't just look at numbers; consider the player's form, injury history, and upcoming schedule.

A great fantasy MLB team is all about balance and foresight. Be sure to look beyond just the star players; consider their supporting cast, underrated or up-and-coming players who often play crucial roles in racking up points (which is key in Sorare MLB, where competitions have player roster point caps).


### Understanding Fantasy Baseball scoring and mechanics

In fantasy baseball, your players' real-life performances are key. Every hit, strikeout, and home run adds points to your team. Pitchers and hitters contribute differently, so understanding these nuances is crucial. Keep an eye on player news and performance trends to make informed decisions.

Scoring in fantasy baseball is where your strategic choices come into play. Types of hits, runs, and RBIs are the most common stats, but not necessarily the most valuable in Sorare MLB; for instance, saves are worth as much as home runs (10 points each), and stolen bases are worth five points each. Each player's performance in real-life games translates into points for your fantasy team. Understanding the scoring system is key to choosing the right players and making smart game-day decisions. Find out more about Sorare MLB scoring [here](https://mlbguide.sorare.com/how-to-play/the-basics/scoring-system).

### Earning cash prizes in Sorare fantasy MLB

One of the exhilarating aspects of fantasy MLB with Sorare is the opportunity to earn cash prizes. By topping leaderboards and outperforming your competitors, you can earn rewards that add a tangible excitement to your fantasy baseball experience. Your strategy and skills in player selection, team management, and strategic foresight are what will set you apart in the quest for these prizes.

### Managing your fantasy MLB team

With Sorare, your fantasy baseball journey is personalized. You choose a unique name for your team, reflecting your style and/or game philosophy. Engaging with Sorare's community is also beneficial; you can exchange tips, discuss strategies, and gain insights from other fantasy managers – as well as buy, sell, and trade with fellow community members/managers on Sorare’s [Marketplace](https://sorare.com/mlb/market). Remember, every decision, from your team name to player transfers, shapes your path in the world of fantasy baseball.

### Essential Strategies for Fantasy Baseball Success

To excel in fantasy baseball, you need a solid strategy. Pay attention to player news, prospect rankings, and DFS insights. Deciding who to start each week is a crucial skill. Monitor player performances closely and be ready to adjust your lineup based on matchups and player conditions.

### Keeping Up with the MLB season and player performances

Staying informed throughout the baseball season is vital. Keep track of player performances, injuries, and team dynamics. Regularly checking stats and news can give you the upper hand in making timely decisions for your team. Being proactive and adapting to the season's rhythm is key to maintaining a competitive edge.

In terms of timing, Sorare fantasy MLB baseball begins in March and ends with a postseason competition that lasts through October and November – in parallel with the MLB season. However, you can jump in to start playing at any time without missing a beat; Sorare competitions are twice-weekly and, aside from special tournaments like the MLB Postseason competition, are not cumulative.

### Maximizing your Fantasy Baseball experience

To maximize your potential in Sorare’s fantasy MLB game – both leaderboard finish and rewards, stay abreast of the latest MLB happenings. Regularly check player stats, team news, and game analyses. Watching games can give you insights beyond what statistics can provide - understanding player roles, team strategies, and even potential breakthrough talents. Another tip is to learn from top fantasy managers. Study their teams, understand their strategies, and see how they adapt to different Sorare competitions and Game Weeks.
    `,
  },
});

export const FAQ = () => {
  const { formatMessage } = useIntl();
  const content = formatMessage(messages.content);

  return (
    <StyledContainer>
      <LazyMarkdown data={content} />
    </StyledContainer>
  );
};
